import {FC, RefObject, memo, useMemo, useRef, useCallback, useState, useEffect} from 'react';
import {Virtuoso, VirtuosoHandle} from 'react-virtuoso';
import {motion, AnimatePresence} from 'framer-motion';
import {useShallow} from 'zustand/react/shallow';
import {Box} from '@chakra-ui/react';
import {useScreenSize} from '@kcast-fe/ui-kit';
import {useMessagesStore} from 'stores';

import {DayInfo, ListFooter, ListHeader, MessageItem} from './components';

// The stuck DayInfo is shown only while the user scrolls; hide it this long
// after the last scroll event (mirrors the mobile behavior).
const STICKY_HIDE_DELAY = 500;
const STICKY_FADE_DURATION = 250;

interface IProps {
  vRef: RefObject<VirtuosoHandle | null>;
  cRef: RefObject<HTMLDivElement | null>;
  onLastVisibleIdx: (idx: number) => void;
}

const MessageListFC: FC<IProps> = ({vRef, cRef, onLastVisibleIdx}) => {
  const [isListScrolling, setIsListScrolling] = useState(false);
  const [topVisibleIdx, setTopVisibleIdx] = useState<number | null>(null);
  const [isStickyVisible, setIsStickyVisible] = useState(false);

  const {chatKey, filter, messages, firstItemIndex, wsActions} = useMessagesStore(
    useShallow((state) => state)
  );

  const {isLessThan768} = useScreenSize();

  const visibleIdx = useRef<number[]>([]);
  const isStickyVisibleRef = useRef(false);
  const stickyHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filterDate = useMemo(() => filter?.date, [filter]);

  // Date of the top-most visible message drives the stuck DayInfo copy.
  const stickyCreatedAt = useMemo(
    () =>
      topVisibleIdx === null ? undefined : messages[topVisibleIdx - firstItemIndex]?.created_at,
    [messages, topVisibleIdx, firstItemIndex]
  );

  const components = useMemo(
    () => ({
      Header: () => <ListHeader />,
      Footer: () => <ListFooter />
    }),
    []
  );

  const handleVisibility = useCallback(
    (idx: number, isVisible: boolean) => {
      if (isVisible) {
        visibleIdx.current.push(idx);
      } else {
        visibleIdx.current = visibleIdx.current.filter((i) => i !== idx);
      }
      if (visibleIdx.current.length > 0) {
        onLastVisibleIdx(Math.max(...visibleIdx.current));
        setTopVisibleIdx(Math.min(...visibleIdx.current));
      }
    },
    [onLastVisibleIdx]
  );

  // Stuck DayInfo visibility: show on a user-initiated scroll (wheel/touch),
  // hide once scroll events stop. Programmatic scrolls (autoscroll on a new
  // message) never reveal it.
  useEffect(() => {
    const el = cRef.current;
    if (!el) {
      return;
    }

    const scheduleHide = () => {
      if (stickyHideTimerRef.current) {
        clearTimeout(stickyHideTimerRef.current);
      }
      stickyHideTimerRef.current = setTimeout(() => {
        isStickyVisibleRef.current = false;
        setIsStickyVisible(false);
      }, STICKY_HIDE_DELAY);
    };

    const onUserScroll = () => {
      isStickyVisibleRef.current = true;
      setIsStickyVisible(true);
      scheduleHide();
    };

    const onScroll = () => {
      if (isStickyVisibleRef.current) {
        scheduleHide();
      }
    };

    el.addEventListener('wheel', onUserScroll, {passive: true});
    el.addEventListener('touchmove', onUserScroll, {passive: true});
    el.addEventListener('scroll', onScroll, {passive: true});

    return () => {
      el.removeEventListener('wheel', onUserScroll);
      el.removeEventListener('touchmove', onUserScroll);
      el.removeEventListener('scroll', onScroll);
      if (stickyHideTimerRef.current) {
        clearTimeout(stickyHideTimerRef.current);
      }
    };
  }, [cRef]);

  return (
    <AnimatePresence mode={'wait'}>
      <motion.div
        key={chatKey} // Triggers exit/enter
        initial={{opacity: 0}}
        animate={{opacity: 1}}
        exit={{opacity: 0}}
        transition={{
          duration: 0.4, // Duration in sec
          delay: 0.5 // Delay before render in sec
        }}
      >
        {/* STUCK DAY INFO: zero-height sticky wrapper spans the whole list */}
        {stickyCreatedAt && (
          <Box
            position={'sticky'}
            top={isLessThan768 ? 14 : 6}
            h={0}
            zIndex={2}
            pointerEvents={'none'}
            opacity={isStickyVisible ? 1 : 0}
            transition={`opacity ${STICKY_FADE_DURATION}ms`}
          >
            <DayInfo createdAt={stickyCreatedAt} />
          </Box>
        )}

        {cRef?.current && (
          <Virtuoso
            ref={vRef}
            data={messages}
            components={components}
            firstItemIndex={firstItemIndex}
            style={{height: '100%', flex: 1}}
            increaseViewportBy={{top: 2000, bottom: 600}}
            skipAnimationFrameInResizeObserver
            computeItemKey={(_, item) => item.id}
            context={{isScrolling: isListScrolling}}
            isScrolling={setIsListScrolling}
            initialTopMostItemIndex={{
              index: filterDate ? 0 : 'LAST',
              align: filterDate ? 'start' : 'end'
            }}
            customScrollParent={cRef?.current ?? undefined}
            startReached={() => wsActions.loadMoreMessages()}
            itemContent={(idx, item) => {
              return <MessageItem msg={item} index={idx} onVisibilityChanged={handleVisibility} />;
            }}
          />
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export const MessageList = memo(MessageListFC);
